import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, Trash2Icon, PencilIcon, CheckIcon } from 'lucide-react';
import { findLocation } from '../../lib/locations';
import { deleteVote, updateVoteEmail } from '../../lib/store';
import type { Vote } from '../../lib/types';
interface Props {
  vote: Vote | null;
  onClose: () => void;
}
export function VoteDetailPanel({
  vote,
  onClose
}: Props) {
  const [editing, setEditing] = useState(false);
  const [email, setEmail] = useState('');
  const loc = vote ? findLocation(vote.city) : undefined;
  function startEdit() {
    if (!vote) return;
    setEmail(vote.email);
    setEditing(true);
  }
  function saveEmail() {
    if (vote) updateVoteEmail(vote.id, email);
    setEditing(false);
  }
  function remove() {
    if (vote && window.confirm('Delete this vote? This cannot be undone.')) {
      deleteVote(vote.id);
      onClose();
    }
  }
  return <AnimatePresence data-id="element-74" data-mp="1kxs7uo97jvan">
      {vote && <>
          <motion.div className="fixed inset-0 z-40 bg-slate-900/30" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} data-id="element-75" data-mp="3p3hvfz4945p" />
          <motion.aside className="fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col bg-white shadow-2xl" initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        stiffness: 320,
        damping: 34
      }} role="dialog" aria-label="Vote details" data-id="element-76" data-mp="3pugtcegse8li">
            <div className="flex items-center justify-between border-b border-slate-100 p-5" data-id="element-77" data-mp="374hc2dr6txsn">
              <h2 className="font-display text-xl font-700 text-slate-800" data-id="element-78" data-mp="326yy28ybcsds">
                Vote Details
              </h2>
              <button onClick={onClose} aria-label="Close details" className="flex h-9 w-9 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700" data-id="element-79" data-mp="2x5t6iege3b5u">
                <XIcon className="h-5 w-5" data-id="element-80" data-mp="2nj8p272p20rs" />
              </button>
            </div>

            <div className="flex-1 space-y-1 overflow-y-auto p-5" data-id="element-81" data-mp="2nh4dd2yjzxgy">
              <div className="mb-5 flex items-center gap-4 rounded-3xl bg-lavender-50 p-4" data-id="element-82" data-mp="29lae7bnl0mh1">
                <span className="text-4xl" data-id="element-83" data-mp="2ejlplqtiz51d">{loc?.emoji ?? '📍'}</span>
                <span data-id="element-84" data-mp="3284udwdd8zop">
                  <span className="block font-display text-lg font-700 text-slate-800" data-id="element-85" data-mp="329o9hjnad4ow">
                    {vote.city}
                  </span>
                  <span className="text-sm text-slate-500" data-id="element-86" data-mp="2silb4plqr3tb">{vote.province}</span>
                </span>
              </div>

              <Field label="Full Name" value={vote.fullName} data-id="element-87" data-mp="2xh4wansigbkm" />

              <div className="border-b border-slate-100 py-3" data-id="element-88" data-mp="2xfbpn8hwdrcm">
                <span className="text-sm text-slate-500" data-id="element-89" data-mp="3l6h5vorr2lng">Email</span>
                {editing ? <div className="mt-1 flex gap-2" data-id="element-90" data-mp="3pyr21mjgkhy6">
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="flex-1 rounded-xl border-2 border-slate-200 px-3 py-1.5 focus:border-lavender-400 focus:outline-none" data-id="element-91" data-mp="37531guegsge" />
                    <button onClick={saveEmail} aria-label="Save email" className="flex h-9 w-9 items-center justify-center rounded-xl bg-teal-500 text-white" data-id="element-92" data-mp="7xttcsucfjdy">
                      <CheckIcon className="h-4 w-4" data-id="element-93" data-mp="3ceeho79a951k" />
                    </button>
                  </div> : <div className="mt-0.5 flex items-center justify-between gap-2" data-id="element-94" data-mp="3lczzu1auei1t">
                    <span className="font-600 text-slate-800" data-id="element-95" data-mp="3lfcmgrrjpgl7">{vote.email}</span>
                    <button onClick={startEdit} className="flex items-center gap-1 text-sm font-600 text-lavender-600 hover:text-lavender-500" data-id="element-96" data-mp="15k0zw8a7jlsy">
                      <PencilIcon className="h-3.5 w-3.5" data-id="element-97" data-mp="36d5wela05jkd" /> Edit
                    </button>
                  </div>}
              </div>

              <Field label="Selected Location" value={`${vote.city}, ${vote.province}`} data-id="element-98" data-mp="3g864xbg5d1np" />
              <Field label="Province" value={vote.province} data-id="element-99" data-mp="3kb2rtecpzjxg" />
              <Field label="Date Submitted" value={vote.date} data-id="element-100" data-mp="3k8hp6n4lkzyn" />
              <Field label="Time Submitted" value={vote.time} data-id="element-101" data-mp="3p9e2frau9ygq" />
              <Field label="Timestamp" value={vote.timestamp} mono data-id="element-102" data-mp="3u8gumb5q0a7a" />
              <Field label="Browser Timezone" value={vote.timezone ?? '—'} data-id="element-103" data-mp="3u6npx1eo401x" />
            </div>

            <div className="border-t border-slate-100 p-5" data-id="element-104" data-mp="2woy2dvlkqzk">
              <button onClick={remove} className="flex w-full items-center justify-center gap-2 rounded-2xl bg-coral-400/10 px-4 py-3 font-600 text-coral-500 transition hover:bg-coral-400/20" data-id="element-105" data-mp="7s4b53ls6iux">
                <Trash2Icon className="h-4 w-4" data-id="element-106" data-mp="grrbrn6orkmx" />
                Delete this vote
              </button>
            </div>
          </motion.aside>
        </>}
    </AnimatePresence>;
}
function Field({
  label,
  value,
  mono
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return <div className="border-b border-slate-100 py-3" data-id="element-107" data-mp="1ncda27y463jk">
      <span className="block text-sm text-slate-500" data-id="element-108" data-mp="1ndonz0l9wroe">{label}</span>
      <span className={`mt-0.5 block font-600 text-slate-800 ${mono ? 'break-all font-mono text-sm' : ''}`} data-id="element-109" data-mp="2k35shc8is7oi">
        {value}
      </span>
    </div>;
}